// src/services/accountService/companyAnnualService.ts
import {BASE_ACCOUNT_API, fetchAPI} from '@/services/api';
import {RequestPage, ResponsePage} from "@/services/page.interface";

export interface CompanyAnnual {
    annualId: number;
    companyId: number;
    companyName: string;

    // --- Period
    periodFrom: string | null;
    periodTo: string | null;
    lastYearPeriodFrom: string | null;
    lastYearPeriodTo: string | null;
    yearOfAssessment: string | null;
    taxFileNo: string | null;
    limitByGuarantee: number;
    /** true when any YOA FS is COMPLETED — UI should disable the flag */
    limitByGuaranteeLocked?: boolean;

    liabilitiesCurrency:string;
    liabilitiesAmount:number;

    otherNameThanCompany: string | null;
    section88: number | null;

    bankConfirmations: BankConfirmation[];

    qualifyOpinion: number;
    firstAudit: number;
    /**
     * @deprecated former 18-month Yes/No — stop writing
     */
    firstAuditFollows18Months?: boolean | null;
    /** Step 2 YE confirm: year-end day/month as `dd/MM` (e.g. `31/03`). */
    firstAuditYearEndMD?: string | null;
    /** Step 1: Is it a multi-year audit? Default true. */
    isMultiYearAudit?: boolean | null;
    /**
     * First audit >18 months: confirm qualified opinion will be raised.
     * 0=unchecked (default), 1=confirmed.
     */
    firstAuditOver18Confirmed?: number | null;
    formerAuditor: string;
    addressOfFormerAuditor: string;
    // AFS --- Pro Clear
    professionalClearance: string | null;

    // Selected audit certificate reference
    auditCertificateId: number;

    auditStaffs: AuditStaff[];

    directors: Director[];

    shareholders: Shareholder[];

    monthsInPeriod: number;
    daysInPeriod: number;
    auditMonths: number;
    isChangeName: number;
    offShoreClaim: number;
    changeEffectiveDate: string | null;
    oldCompanyName: string | null;
    oldCompanyChineseName: string | null;
    /** Period-resolved EN name (YOA); used when toggling No→Yes */
    currentCompanyName?: string | null;
    /** Period-resolved ZH name (YOA) */
    currentCompanyChineseName?: string | null;
    /** Latest / editing new EN name (from account_company when Yes) */
    companyChineseName?: string | null;
    changeOfFinancialYearEnd: number;
    dateOfBoardResolution: string | null;
    reasonToChange: string | null;
    cessationDuringThisPeriod: number;

    presentationCurrency: string;
    exchangeRate: number;
    functionalCurrency: string | null;

    dividendPaid: number;
    dividendCurrency: string | null;
    amountPerEachShare: number;
    totalDividendAmount: number;
    dividendPayableBf?: number | null;
    dividendDeclared?: number | null;
    dividendPaidAmount?: number | null;
    twoTierTaxThisYear: number;
    twoTierTaxLastYear: number;
    assessableResultType: number;
    assessableAmount: number;
    taxLossBF: number;
    taxLossCF: number;

    // ===== 地址与变更 =====
    registeredOffice: string | null;
    currentRegisteredOffice?: string | null;
    changeOfAddress: number;
    lastAddress: string | null;
    changeDate: string | null;
    ppe: number;
    relatedPartyTransactions: number;
    rentalCommitment: number;
    // ===== 股权 =====
    holdingCo: number;
    nameOfImmediate: string | null;
    immediateHolding: string | null;
    nameOfUltimate: string | null;
    ultimateHolding: string | null;

    allotmentOfShares: number;
    allotmentAmount: number;
    noOfShares: number;
    pricePerShare: number;
    priceCurrency: string | null;
    dividendPaidOfOverseas: number;
    overseasDividendCurrency: string | null;
    overseasDividendAmountPerShare: number;
    overseasTotalDividendAmount: number;
    principalActivities: number;
    businessNature: string | null;
    commenceWithinPeriod: number;
    commenceDate: string | null;

    // letterOfSupport: string | null;
    netLiabilities: number;
    pettyCashConfirmation: string;
    pettyCashAmount: number;

    irdLetterSigner: string;

    /** 1 = Tax Rep from certificate; 0 = saved tax rep / manual */
    taxRepSameAsAuditor?: number | null;
    taxRepName?: string | null;
    taxRepAddress?: string | null;
    taxRepTel?: string | null;
    taxRepBrNumber?: string | null;
    /** Selected audit certificate id for Tax Rep (may differ from auditCertificateId) */
    taxRepCertificateId?: number | null;
    /** FK to admin_tax_rep when using saved / manual tax rep */
    taxRepId?: number | null;
    /** Hereby authorize snapshot */
    taxRepHerebyAuthorize?: string | null;

    referrer: string | null;
    inCharge: string | null;
}

export interface SharesChangeRecord {
    id: number;
    annualId: number;
    shareholderName: string | null;
    currency: string | null;
    valueOfEachShare: number | null;
    exchangeRate: number | null;
    transferOfShares: number | null;
    allotmentOfShares: number | null;
    changeDate: string | null;
}

export interface BankConfirmation {
    id: number;
    bankName: string;
    accountNumber: string;
    isValid: number;
}

export interface AuditStaff {
    userId: number;
    auditStaffName: string | null;
    post: string | null;
    initial: string | null;
    roleType: number; // 1=Preparer, 2=Reviewer, 3=Partner
}

export interface Shareholder {
    id: number | null;
    shareholderName: string | null;
    gender: number | null;
    acquireDuringPeriod: string | null;
    currency: string | null;
    noOfShares: number | null;
    /** 股东币种 → presentationCurrency 汇率（买入时基准），同币默认 1 */
    exchangeRate: number | null;
    transferOfShares: number | null,
    allotmentOfShares: number | null;
    valueOfEachShare: number | null;
    /** 用户手动指定的总价覆盖值（split mode）；非 null 时以该值为准，可为负/0 */
    totalPriceOverride?: number | null;
    /** 后端计算型只读字段：override != null → override，否则 parValue × currentShares × exchangeRate */
    effectiveFinalPrice?: number | null;
}

export interface TransferOfShares {
    id: number | null;
    shareholderId: number | null;
    transferDate: string | null;
    currency: string | null;
    valueOfEachShare: number | null;
    exchangeRate: number | null;
    transferShares: number | null;
}

export interface AllotmentOfShares {
    id: number | null;
    shareholderId: number | null;
    allotmentDate: string | null;
    currency: string | null;
    valueOfEachShare: number | null;
    exchangeRate: number | null;
    allotmentShares: number | null;
}

export interface Director {
    id: number | null;
    name: string;
    appointmentDate: string | null;
    resignDate: string | null;
    /** 身份证/护照号码（来自 account_company_director.id_no） */
    idNo?: string | null;
}

// Payload used for create/update where backend expects `auditStaffId`
export type CompanyAnnualPayload = Omit<CompanyAnnual, 'auditStaffs'> & {
    auditStaffs: Array<{
        userId: number;
        roleType: number; // 1=Preparer, 2=Reviewer, 3=Partner
    }>;
};

export interface CompanyAnnualPage {
    annualId: number | 0;
    companyId: number | 0;
    companyName: string | null;
    companyChineseName: string | null;
    periodFrom?: string | null;
    periodTo?: string | null;
    yearOfAssessment: string | null;
    principalActivities: string | null;
    presentationCurrency: string | null;
    qualifyOpinion: string | null;
    flowType?: number | null;
    flowName?: string | null;
    fsStatus?: number | null; // 1=ACCOUNT_EDITING, 2=AUDIT_EDITING, 3=AUDIT_ADJUSTMENT, 4=COMPLETED
}

// Selection list for a given company (used in steppers/selects)
export interface CompanyAnnualSelectItem {
    annualId: number;
    yearOfAssessment: string | null;
    periodFrom: string | null;
    periodTo: string | null;
    taxFileNo: string | null;
    shareholders: Array<{
        id: number;
        shareholderName: string | null;
        gender: number;
    }>;
}

// Guessing base path per existing conventions
// If backend differs, adjust `base` and endpoints accordingly
const base = BASE_ACCOUNT_API + '/companyAnnual';

export async function getAnnual(id: number) {
    return fetchAPI<CompanyAnnual>(`${base}/${id}`);
}

export async function annualPage(companyId: number, request: RequestPage) {
    return fetchAPI<ResponsePage<CompanyAnnual>>(
        `${base}/queryPage/${companyId}`, {
            method: 'POST',
            body: JSON.stringify(request),
        }
    );
}

export interface RequestCompanyAnnualPage extends RequestPage {
    companyName?: string;
    yearOfAssessment?: string;
    flowType?: number;
    fsStatus?: number;
}

export async function companyAnnualPage(request: RequestCompanyAnnualPage) {
    return fetchAPI<ResponsePage<CompanyAnnualPage>>(
        `${base}/queryPage`, {
            method: 'POST',
            body: JSON.stringify(request),
        }
    );
}

export async function createAnnual(bo: CompanyAnnualPayload) {
    return fetchAPI<CompanyAnnual>(`${base}/create`, {
        method: 'POST',
        body: JSON.stringify(bo),
    });
}

export async function updateAnnual(id: number, bo: CompanyAnnualPayload) {
    return fetchAPI<CompanyAnnual>(`${base}/update/${id}`, {
        method: 'POST',
        body: JSON.stringify(bo),
    });
}

export async function deleteAnnual(id: number) {
    return fetchAPI<void>(`${base}/delete/${id}`, {method: 'POST'});
}

export async function querySelectionList(companyId: number) {
    return fetchAPI<CompanyAnnualSelectItem[]>(`${base}/querySelectionList?companyId=${companyId}`, {method: 'GET'});
}

/** Active company_shareholder rows (master ids) for Tenant / identity sync. */
export async function selectShareholder(companyId: number) {
    return fetchAPI<Array<{
        id: number;
        name?: string | null;
        identificationNumber?: string | null;
        idNo?: string | null;
    }>>(`${base}/selectShareholder?companyId=${companyId}`, {method: 'GET'});
}

export async function getInfoByScheduleCode(scheduleCode: string, annualId: number) {
    return fetchAPI<any>(`${base}/getInfoByScheduleCode?scheduleCode=${scheduleCode}&annualId=${annualId}`, {method: 'GET'});
}